import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import { monthBounds, monthPhase, currentMonthKey } from '../../lib/months';
import {
  computeReleasePhases, spanInMonth, sortReleasesByStart,
  colorForLane, colorForPhase, type ReleaseCal,
} from '../../lib/releases';
import { formatDate } from '../../lib/format';

const LANE_BAND_H = 18;
const LANE_PHASE_H = 16;
const LANE_GAP = 3;
const LANE_HEIGHT = LANE_BAND_H + LANE_PHASE_H + LANE_GAP;

type Props = {
  months: string[];
  releaseCal: Record<string, ReleaseCal>;
};

// Release-Layer: pro Release ein Hauptband (oben) + Phasen-Bänder (unten).
// Lane-Logic: jeder Release bekommt eine eigene Y-Spur, sodass sich Bänder
// nicht überlappen.
export function ReleaseLayer({ months, releaseCal }: Props) {
  const releases = sortReleasesByStart(releaseCal);
  if (!releases.length) {
    return (
      <Row label="📅 Release-Layer" sticky months={months}>
        <Box sx={{ p: 1.5, color: 'text.secondary', fontStyle: 'italic', fontSize: 12 }}>
          Noch keine Releases mit Start/Ende gepflegt — kommt in Phase 2.6 als Modal.
        </Box>
      </Row>
    );
  }

  const rowHeight = Math.max(56, releases.length * LANE_HEIGHT + 8);
  const cur = currentMonthKey();

  // Label-Tracker: ein Release-Name wird nur in der ersten sichtbaren Zelle
  // der Timeline gerendert, in der das Band beginnt.
  const labelShown = new Set<string>();

  return (
    <Box
      role="row"
      sx={{
        display: 'grid',
        gridTemplateColumns: `200px repeat(${months.length}, minmax(60px, 1fr)) 70px`,
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <Box sx={{ p: 1, height: rowHeight, borderRight: 1, borderColor: 'divider' }}>
        <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 700, lineHeight: 1.2, display: 'block' }}>
          Releases & Service Packs
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, lineHeight: 1.3 }}>
          {releases.map((name, i) => (
            <Box component="span" key={name} sx={{ color: colorForLane(i), fontWeight: 700, mr: 0.75 }}>
              {name}{i < releases.length - 1 ? ' ·' : ''}
            </Box>
          ))}
        </Typography>
      </Box>

      {months.map(mk => {
        const mb = monthBounds(mk);
        const phase = monthPhase(mk, cur);
        return (
          <Box
            key={mk}
            sx={{
              position: 'relative',
              height: rowHeight,
              borderLeft: 1,
              borderColor: 'divider',
              bgcolor: phase === 'now' ? 'primary.main' : 'transparent',
              opacity: phase === 'now' ? 0.06 : 1,
              ...(phase === 'now' && { '&::after': { content: '""', position: 'absolute', inset: 0, bgcolor: 'primary.main', opacity: 0.08, pointerEvents: 'none' } }),
            }}
          >
            {releases.map((name, laneIdx) => {
              const cal = releaseCal[name];
              if (!cal?.start || !cal?.end) return null;
              const calStart = new Date(cal.start);
              const calEnd   = new Date(cal.end);
              const phases = computeReleasePhases(cal);
              const laneTop = laneIdx * LANE_HEIGHT;
              const phaseTop = laneTop + LANE_BAND_H;
              const color = colorForLane(laneIdx);

              const bandSpan = spanInMonth(calStart, calEnd, mb);
              const showLabel = bandSpan && !labelShown.has(name);
              if (showLabel) labelShown.add(name);

              return (
                <Box key={name}>
                  {bandSpan && (
                    <Tooltip
                      arrow
                      title={`${name}: ${formatDate(cal.start)} → ${formatDate(cal.end)}`}
                    >
                      <Box
                        sx={{
                          position: 'absolute',
                          left:  `${bandSpan.leftPct}%`,
                          width: `${bandSpan.widthPct}%`,
                          top: laneTop,
                          height: LANE_BAND_H,
                          bgcolor: color,
                          color: 'white',
                          fontSize: 10,
                          fontWeight: 700,
                          px: 0.75,
                          display: 'flex',
                          alignItems: 'center',
                          borderRadius: 1,
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                          textShadow: '0 1px 1px rgba(0,0,0,0.25)',
                        }}
                      >
                        {showLabel ? name : ''}
                      </Box>
                    </Tooltip>
                  )}

                  {phases.map((p, i) => {
                    const span = spanInMonth(p.start, p.end, mb);
                    if (!span) return null;
                    const isFirstCell = p.start >= mb.start && p.start <= mb.end;
                    return (
                      <Tooltip
                        key={i}
                        arrow
                        title={`${name} · ${p.name}: ${formatDate(p.start)} → ${formatDate(p.end)}`}
                      >
                        <Box
                          sx={{
                            position: 'absolute',
                            left:  `${span.leftPct}%`,
                            width: `${span.widthPct}%`,
                            top: phaseTop,
                            height: LANE_PHASE_H,
                            bgcolor: colorForPhase(p),
                            opacity: 0.85,
                            color: 'white',
                            fontSize: 9,
                            fontWeight: 600,
                            px: 0.5,
                            display: 'flex',
                            alignItems: 'center',
                            borderRadius: 1,
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {isFirstCell ? p.name : ''}
                        </Box>
                      </Tooltip>
                    );
                  })}

                  {(cal.sps ?? []).filter(sp => sp.name && sp.date).map(sp => {
                    const d = new Date(sp.date);
                    if (isNaN(d.getTime()) || d < mb.start || d > mb.end) return null;
                    const outside = d < calStart || d > calEnd;
                    const leftPct = ((d.getTime() - mb.start.getTime()) / 86400000 + 1) / mb.totalDays * 100;
                    return (
                      <Tooltip key={sp.name} arrow title={`${sp.name} – ${formatDate(sp.date)}${outside ? ' (außerhalb!)' : ''}`}>
                        <Box
                          sx={{
                            position: 'absolute',
                            left: `calc(${leftPct}% - 1px)`,
                            top: laneTop + LANE_BAND_H,
                            height: LANE_PHASE_H,
                            width: 2,
                            bgcolor: outside ? 'warning.main' : color,
                            borderRadius: 1,
                          }}
                        />
                      </Tooltip>
                    );
                  })}
                </Box>
              );
            })}
          </Box>
        );
      })}

      <Box sx={{ borderLeft: 1, borderColor: 'divider', height: rowHeight }} />
    </Box>
  );
}

// Simple wrapper für leere/Info-Zeilen (z.B. wenn kein Release-Kalender da)
function Row({ label, months, children }: { label: string; sticky?: boolean; months: string[]; children: React.ReactNode }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: `200px repeat(${months.length}, minmax(60px, 1fr)) 70px`,
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <Box sx={{ p: 1, fontSize: 11, fontWeight: 700, color: 'primary.main', textTransform: 'uppercase' }}>{label}</Box>
      <Box sx={{ gridColumn: `2 / span ${months.length + 1}` }}>{children}</Box>
    </Box>
  );
}
